import {Dispatch, FC, SetStateAction, useEffect} from "react";
import {EditorContent, Extension, useEditor} from "@tiptap/react";
import Link from '@tiptap/extension-link'
import StarterKit from "@tiptap/starter-kit";
import {BulletList, ListItem, OrderedList} from "@tiptap/extension-list";
import TextAlign from "@tiptap/extension-text-align";
import Heading from "@tiptap/extension-heading";
import {TopMenuBar} from "./components/menu/topMenu";
import {useDocumentationLibraryContext} from "./context/DocumentationLibraryContext";
import moment from "moment";

interface TipTapEditorProps {
  showMenuBar: boolean;
  editable: boolean;
  content: string;
  title: string;
  lastEdited: Date;
  setContent?: Dispatch<SetStateAction<string>>;
  topOffset?: 'top-0'|'top-12'
  menuBarType?: "top"
}

export const TipTapEditor: FC<TipTapEditorProps> = ({ showMenuBar, editable, content, setContent, topOffset, menuBarType, title, lastEdited }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: false,
        orderedList: false,
        listItem: false,
        heading: false,
        link: false,
      }),
      ListItem,
      BulletList.configure({
        keepMarks: true,
        keepAttributes: true,
      }),
      OrderedList,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Heading.configure({
        levels: [1, 2],
      }),
      Link.configure({
        openOnClick: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        isAllowedUri: (url, ctx) => ctx.defaultValidate(url) && !url.startsWith('./'),
      }),
      LiteralTab,
    ],
    content: content,
    parseOptions: {
      preserveWhitespace: true,
    },
    editorProps: {
      attributes: {
        class: 'bg-white py-10 px-8 max-w-(--breakpoint-md) mx-auto',
      },
    },
    onUpdate: ({ editor }) => {
      if (setContent) {
        setContent(editor.getHTML());
      }
    },
  });

  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  return <>
    <style>{`
      .editor {
        font-family: 'Open Sans', sans-serif;
      }
      .editor h1 {
        font-size: 24px;
        font-weight: bold;
      }
      .editor h2 {
        font-size: 18px;
        font-weight: bold;
      }
      .editor ul,ol {
        padding-left: 1.5em;
      }
      .editor ul > li {
        list-style-type: disc;
      }
      .editor ol > li {
        list-style-type: number;
      }
      .editor a {
        text-decoration: underline;
        color: blue;
      }
    `}</style>
    <div className={"bg-white border-t border-slate-200"}>
      <div className={'max-w-(--breakpoint-md) mx-auto px-8 mb-6'}>
        <h1 className={'text-3xl font-bold mt-8 mb-2'}>{title}</h1>
        <div className={'text-xs text-slate-600'}>Laatste wijziging: {moment(lastEdited).locale('nl-NL').fromNow()}</div>
      </div>
      {(showMenuBar && editable && editor && menuBarType === 'top') ?
        <div className={"sticky top-0 z-50 bg-white"}>
          <TopMenuBar editor={editor} />
        </div> : <div></div>
      }



      <div className={"editor min-h-[calc(100vh-12.5rem)] flex flex-col items-stretch"}>
        <EditorContent editor={editor} className={"flex-1 [&_.ProseMirror]:h-full"}/>
      </div>
    </div>
  </>;
}

const LiteralTab = Extension.create({
  name: 'literalTab',

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        return this.editor.commands.insertContent('\u0009')
      }
    }
  }

})